import React, { useEffect } from 'react'
import {
  Alert,
  Button,
  Col,
  Form,
  Modal,
  Row,
  useToaster
} from 'react-angle-dashboard-components'

import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { ReactComponent as ExclaimationTriangle } from '../../assets/exclaimationTriangle.svg'
import { useMarkQuoteAsSold, useQuote } from '../../hooks/quote.hooks'
import {
  mapSoldQuoteDTOToForm,
  mapSoldQuoteFormToCreateDTO
} from '../../services/quote/mappers'

import './SoldQuoteFormModal.css'

const soldQuoteFormSchema = z.object({
  groupName: z.string().min(1, 'Employer name is required'),
  effectiveDate: z.string().min(1, 'Effective Date is required'),
  groupContactFirstName: z
    .string()
    .min(1, 'Group Contact first name is required'),
  groupContactLastName: z.string().min(1, 'Group Contact last name is required')
})

export type ISoldQuoteForm = z.infer<typeof soldQuoteFormSchema>

interface SoldQuoteFormModalProps {
  open: boolean
  onClose: () => void
  quoteIdToEdit: string
}

const SoldQuoteFormModal = ({
  open,
  onClose,
  quoteIdToEdit
}: SoldQuoteFormModalProps) => {
  const { data: quote } = useQuote(quoteIdToEdit)
  const { showError } = useToaster()
  const { mutateAsync: markQuoteAsSoldMutate } = useMarkQuoteAsSold()
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ISoldQuoteForm>({
    resolver: zodResolver(soldQuoteFormSchema)
  })

  useEffect(() => {
    if (quote) {
      reset(mapSoldQuoteDTOToForm(quote))
    }
  }, [quoteIdToEdit, quote, reset])

  const markQuoteAsSold = async (formData: ISoldQuoteForm) => {
    await markQuoteAsSoldMutate({
      quoteId: quoteIdToEdit,
      data: mapSoldQuoteFormToCreateDTO(formData)
    })
  }

  const onSubmit = async (formData: ISoldQuoteForm) => {
    try {
      await markQuoteAsSold(formData)
      onClose()
    } catch (err) {
      console.error(err)
      showError('An error occured trying to mark the quote as sold')
    }
  }

  return (
    <Modal
      show={open}
      centered
      onHide={onClose}
      dialogClassName="soldQuoteFormModalDialog"
      className="soldQuoteFormModal"
      style={{
        zIndex: 2000
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Almost there! We need you to provide the following information:
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Employer Name</Form.Label>
              <fieldset disabled>
                <Form.Control
                  isInvalid={!!errors?.groupName}
                  {...register('groupName')}
                  style={{ cursor: 'not-allowed' }}
                />
              </fieldset>
              <Form.Control.Feedback type="invalid">
                {errors?.groupName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Effective Date</Form.Label>
              <Form.Control
                isInvalid={!!errors?.effectiveDate}
                {...register('effectiveDate')}
              />
              <Form.Control.Feedback type="invalid">
                {errors?.effectiveDate?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Group Contact First Name</Form.Label>
              <Form.Control
                isInvalid={!!errors?.groupContactFirstName}
                placeholder="First Name"
                {...register('groupContactFirstName')}
              />
              <Form.Control.Feedback type="invalid">
                {errors?.groupContactFirstName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Group Contact Last Name</Form.Label>
              <Form.Control
                isInvalid={!!errors?.groupContactLastName}
                placeholder="Last Name"
                {...register('groupContactLastName')}
              />
              <Form.Control.Feedback type="invalid">
                {errors?.groupContactLastName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Alert
            style={{
              backgroundColor: '#FFF3CD',
              marginLeft: '15px',
              width: '750px'
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <ExclaimationTriangle
                  className="mr-3"
                  style={{ width: '50px' }}
                />
                Please note that upon sale, our Angle Health team will be in
                contact with you and your group for next steps to begin
                implementation. If you are not currently appointed with Angle
                Health, our team will reach out to you to finalize the necessary
                information for appointment before proceeding with your group's
                implementation process.
              </div>
            </div>
          </Alert>
        </Row>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="outline-primary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit(onSubmit)}>Create</Button>
      </Modal.Footer>
    </Modal>
  )
}

export default SoldQuoteFormModal
